import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
    getDateWithoutTimezone,
    getTimeWithoutTimezone,
} from "../../../common/helpers";
import { l10n } from "../../../common/constants";
import { Button, InputGroup, Modal, Form } from "react-bootstrap";
import { useMinistries } from "../../../lib/vmapi";
import PreviewableImage from "../../common/PreviewableImage";
import DynamicRegistrationForm from "../../form-builder/DynamicRegistrationForm";
import {
    getAnswerDetails,
    getFormDefinition,
    getPluginKey,
    getSeragamGender,
    getSeragamSize,
    getSessionContext,
    PLUGIN_MISSION_TRIP,
    PLUGIN_SERAGAM,
} from "../../form-builder/schemaRuntime";

const seragamExclusives = Array.from(
    { length: 20 },
    (_, index) => `seragam${index + 1}`
);
const MISSION_TRIP_GSC_EXCLUSIVE = "missiontripgsc";
const MISSION_TRIP_EXCLUSIVE = "missiontrip";
const MISSION_TRIP_MEALBOX = "Mealbox Only";
const MISSION_TRIP_FULL_PACKAGE = "Paket Akomodasi";
const AOG_HUB_FACILITATOR = "Facilitator Group Discussion";
const AOG_HUB_PLAN_ABROAD = "Berencana Kuliah di Luar Negeri";
const AOG_HUB_PLAN_DOMESTIC = "Berencana Kuliah di Dalam Negeri / Dosmetik";
const missionTripMinistryOptions = ["Usher", "Prayer"];
const missionTripRoommateRelationshipOptions = [
    "Teman",
    "Keluarga (suami istri)",
    "Keluarga (kakak adik)",
    "Keluarga (orang tua - anak)",
];
const missionTripGeneralTerms = [
    "Paket yang telah dibeli tidak dapat dibatalkan atau diuangkan kembali.",
    "Peserta bersedia mengikuti seluruh rangkaian kegiatan Mission Trip sesuai arahan dan jadwal yang ditetapkan oleh Panitia.",
];
const missionTripKupangAssets = {
    cover: "https://cf-assets.gms.church/f7b8b0c0-4d6d-4e4c-2fd5-21e883bb7f00/960",
    scheduleFirst: "https://cf-assets.gms.church/4cb6fced-072a-4930-79f9-d26d5d0dd400/960",
    scheduleSecond: "https://cf-assets.gms.church/db10bd0b-f2d1-4e71-54e9-4eb3145b9c00/960",
    commodius: "https://cf-assets.gms.church/5fa14748-9b6d-48c1-3451-2a060f4f5f00/960",
    onTheRock: "https://cf-assets.gms.church/fbbc54b2-0a01-42b1-3e87-fcb777ca7c00/960",
    harper: "https://cf-assets.gms.church/8d3b3b04-cc9a-41c5-28d7-43f582cf8900/960",
};
const missionTripScheduleImages = [
    missionTripKupangAssets.scheduleFirst,
    missionTripKupangAssets.scheduleSecond,
];

const getMissionTripPackageType = (name = "") =>
    /meal\s*box|nasi\s*kotak/i.test(name)
        ? MISSION_TRIP_MEALBOX
        : MISSION_TRIP_FULL_PACKAGE;

const getMissionTripMealboxServiceTerm = (name = "") => {
    const normalizedName = name.toLowerCase();
    const isAugustSeven =
        /\b0?7\b/.test(normalizedName) ||
        /hari\s*ke\s*1/.test(normalizedName) ||
        normalizedName.includes("stb");
    const isAugustEight =
        /\b0?8\b/.test(normalizedName) ||
        /hari\s*ke\s*2/.test(normalizedName) ||
        normalizedName.includes("kkr");
    const isBothDays =
        isAugustSeven &&
        (isAugustEight ||
            /hari\s*ke\s*1.*(?:dan|&|\+|\/).*2/.test(normalizedName) ||
            /\b0?7\b.*(?:dan|&|\+|\/).*0?8\b/.test(normalizedName));

    if (isBothDays) {
        return "Voucher nasi kotak disediakan khusus bagi Pelayan Tuhan yang bertugas dalam pelayanan STB maupun KKR AOG.";
    }

    if (isAugustEight) {
        return "Voucher nasi kotak disediakan khusus bagi Pelayan Tuhan yang bertugas dalam pelayanan KKR AOG.";
    }

    return "Voucher nasi kotak disediakan khusus bagi Pelayan Tuhan yang bertugas dalam pelayanan STB.";
};

const getMissionTripFallbackBannerUrl = (name = "") => {
    const normalizedName = name.toLowerCase();
    if (/meal\s*box|nasi\s*kotak/.test(normalizedName)) {
        return "";
    }

    if (
        normalizedName.includes("commodius") ||
        normalizedName.includes("commotidius") ||
        normalizedName.includes("coomodius")
    ) {
        return missionTripKupangAssets.commodius;
    }

    if (normalizedName.includes("on the rock") || normalizedName.includes("rock")) {
        return missionTripKupangAssets.onTheRock;
    }

    if (normalizedName.includes("harper")) {
        return missionTripKupangAssets.harper;
    }

    return missionTripKupangAssets.cover;
};

const getMissionTripBannerUrl = (eventSub = {}) => {
    const imageUrl = eventSub.banner_url || eventSub.image_url;
    if (!imageUrl) {
        return getMissionTripFallbackBannerUrl(eventSub.name || "");
    }

    return imageUrl.startsWith("http")
        ? imageUrl
        : `${process.env.REACT_APP_IMAGES_URL}${imageUrl}`;
};

const defaultSeragamGroup = "Pilihan Ukuran";
const seragamGroupLabels = {
    Pria: "seragamGroupMan",
    Wanita: "seragamGroupWoman",
    Anak: "seragamGroupKids",
    [defaultSeragamGroup]: "seragamGroupDefault",
};

const getSeragamGroup = (name = "") => {
    const groupMatch = name.match(/\[(.*?)\]/);
    if (groupMatch && groupMatch[1]) {
        return groupMatch[1].trim();
    }

    const normalizedName = name.toLowerCase();
    if (normalizedName.includes("pria")) {
        return "Pria";
    }

    if (normalizedName.includes("wanita")) {
        return "Wanita";
    }

    if (normalizedName.includes("anak")) {
        return "Anak";
    }

    return defaultSeragamGroup;
};

const getSeragamSizeName = (name = "") =>
    name.replace(/\s*\[(.*?)\]\s*/g, "").trim();

const isSeragamCustomSizeSession = (name = "") =>
    name.toLowerCase().includes("custom size");

export default function ModalOrder(props) {
    const { language } = useSelector((state) => ({
        language: state.userReducer.language,
    }));

    const [minQtyViewSeatsLeft, setMinQtyViewSeatsLeft] = useState(100);
    const [agreeOverflow, setAgreeOverflow] = useState(false);
    const { kelasOptions, onChangeKelas } = props;
    const [kelas, setKelas] = useState("");
    const {akomodasiOptions, onChangeAkomodasi} = props;
    const [akomodasi, setAkomodasi] = useState("");
    const {hotelOptions, onChangeHotel} = props;
    const [hotel, setHotel] = useState("");
    const {tipekamarOptions, onChangeTipeKamar} = props;
    const [tipekamar, setTipeKamar] = useState("");
    const [kelasOther, setKelasOther] = useState("");
    const [showMissionTripScheduleModal, setShowMissionTripScheduleModal] =
        useState(false);
    const [showOrderConfirmationModal, setShowOrderConfirmationModal] =
        useState(false);
    const txtOther = useRef(null);
    const exclusive = localStorage.getItem("exclusive");
    const isVMEvent = /^vm\d$/.test(props.selectedEvent.exclusive);
    const {
        data: ministries,
        isLoading: ministriesLoading,
        isError: ministriesError,
    } = useMinistries(isVMEvent);

    useEffect(() => {
        if (props.selectedEventSub.hasOwnProperty("room")) {
            if (
                props.selectedEventSub.room.hasOwnProperty("estimated_quantity")
            ) {
                const { estimated_quantity } = props.selectedEventSub.room;
                setMinQtyViewSeatsLeft(estimated_quantity * (20 / 100));
            }
        }
    }, [minQtyViewSeatsLeft, props.selectedEventSub]);

    const otherChecked = kelas === "Other";
    useEffect(() => {
        if (kelas === "Other") {
            txtOther.current.focus();
            onChangeKelas(kelasOther);
            return;
        }
        onChangeKelas(kelas);
    }, [kelas, kelasOther, onChangeKelas]);

    const akomodasiChecked = akomodasi !== "tidak" && akomodasi !== "";
    useEffect(() => {
        if (akomodasi === "tidak") {
            onChangeAkomodasi(akomodasi);
            setHotel("");
            setTipeKamar("");
            return;
        }
        onChangeAkomodasi(akomodasi);
        onChangeHotel(hotel);
        onChangeTipeKamar(tipekamar);
    }, [akomodasi, hotel, tipekamar, onChangeAkomodasi, onChangeHotel, onChangeTipeKamar]);


    const aogFindingMajorCheck =
        exclusive === "aogfindingmajor" &&
        (kelas === "" || (kelas === "Other" && kelasOther === ""));
    // AOG BootCamp
    const { registrationTypeOptions, onChangeRegistrationType } = props;
    const [registrationType, setRegistrationType] = useState("mandiri");
    useEffect(() => {
        onChangeRegistrationType(registrationType);
    }, [registrationType, onChangeRegistrationType]);

    const { registrantName, onChangeRegistrantName } = props;
    const { registrantAge, onChangeRegistrantAge } = props;
    const { genderOptions, onChangeGender } = props;
    const [gender, setGender] = useState("pria");
    useEffect(() => {
        onChangeGender(gender);
    }, [gender, onChangeGender]);

    const { phoneNumber, onChangePhoneNumber } = props;

    

    const { isCG, onChangeIsCG } = props;

    const { ministry, onChangeMinistry } = props;

    const { medicalCondition, onChangeMedicalCondition } = props;
    const { catatan, onChangeCatatan } = props;

    const [ticketQty, setTicketQty] = useState("1");
    const onInputTicketBlur = () => {
        const maxTicketOrder = Number(props.maxTicketOrder);
        let ticketQuantity = Number(ticketQty);
        if (ticketQuantity > maxTicketOrder) ticketQuantity = maxTicketOrder;
        else if (ticketQuantity < 1) ticketQuantity = 1;
        setTicketQty(ticketQuantity);
        props.onChangeTicketQuantity(ticketQuantity);
    };

    const increaseTicketQty = () => {
        const qty = Number(ticketQty);
        if (qty < Number(props.maxTicketOrder)) {
            setTicketQty(qty + 1);
            props.onChangeTicketQuantity(qty + 1);
        }
    };

    const decreaseTicketQty = () => {
        const qty = Number(ticketQty);
        if (qty > 1) {
            setTicketQty(qty - 1);
            props.onChangeTicketQuantity(qty - 1);
        }
    };

    const aogBootcampCheck =
        (exclusive === "aogbootcamp1" || exclusive === "aogbootcamp1regional" || exclusive === "aogbootcamp2" || exclusive === "aogbootcamp2regional") &&
        (phoneNumber === "" || medicalCondition === "" || (registrationType === "wali" && (registrantName === "" || registrantAge === "")));

    const christmasVipCheck =
        ( exclusive === "christmascelebrationforvip" || 
          exclusive === "christmascelebrationforvip1" || 
          exclusive === "christmascelebrationforvip2" || 
          exclusive === "christmascelebrationforvip3" || 
          exclusive === "christmascelebrationforvip4" || 
          exclusive === "christmascelebrationforvip5" || 
          exclusive === "stbinvitation" ) && catatan === "";
    const isAogHub = props.selectedEvent.exclusive === "aoghub";
    const isAogHubOnline = props.selectedEvent.exclusive === "aoghubonline";
    const isAogHubFacilitator =
        props.aogHubStudyStatus === AOG_HUB_FACILITATOR;
    const isAogHubPlanning =
        props.aogHubStudyStatus === AOG_HUB_PLAN_ABROAD ||
        props.aogHubStudyStatus === AOG_HUB_PLAN_DOMESTIC;
    const aogHubOrigin = (props.origin || "").trim();
    const aogHubDestination = (props.destination || "").trim();
    const aogHubCheck =
        isAogHub &&
        (props.aogHubStudyStatus === "" ||
            aogHubOrigin === "" ||
            (isAogHubPlanning && aogHubDestination === ""));
    const aogHubOnlineCheck =
        isAogHubOnline && (aogHubOrigin === "" || aogHubDestination === "");
    const formDefinition = getFormDefinition(props.selectedEvent);
    const dynamicPluginKey = getPluginKey(props.selectedEvent);
    const isDynamicForm = !!formDefinition;
    const isSeragamExclusive =
        seragamExclusives.includes(exclusive) ||
        dynamicPluginKey === PLUGIN_SERAGAM;
    const vmCheck = /^vm\d$/.test(exclusive) && !ministry;

    const isMissionTripGsc = exclusive === MISSION_TRIP_GSC_EXCLUSIVE;
    const isMissionTripBehavior =
        exclusive === MISSION_TRIP_EXCLUSIVE ||
        dynamicPluginKey === PLUGIN_MISSION_TRIP;
    const isMissionTrip =
        exclusive === MISSION_TRIP_EXCLUSIVE && !isDynamicForm;
    const dynamicSessionContext = formDefinition
        ? getSessionContext(formDefinition, props.selectedEventSub, language)
        : null;
    const missionTripPackageType = dynamicSessionContext
        ? dynamicSessionContext.package_label
        : getMissionTripPackageType(props.selectedEventSub.name || "");
    const isMissionTripFullPackage =
        isMissionTripBehavior &&
        (dynamicSessionContext
            ? dynamicSessionContext.package_type === "accommodation"
            : missionTripPackageType === MISSION_TRIP_FULL_PACKAGE);
    const missionTripBannerUrl = isMissionTripBehavior
        ? (formDefinition && formDefinition.config && formDefinition.config.banner_url) ||
          getMissionTripBannerUrl(props.selectedEventSub)
        : "";
    const missionTripMealboxServiceTerm = getMissionTripMealboxServiceTerm(
        props.selectedEventSub.name || ""
    );
    const missionTripPelayananAlternatif =
        props.missionTripPelayananAlternatif || "";
    const missionTripPunyaTemanSekamar =
        props.missionTripPunyaTemanSekamar || "";
    const missionTripNamaTemanSekamar = (
        props.missionTripNamaTemanSekamar || ""
    ).trim();
    const missionTripNijTemanSekamar = (
        props.missionTripNijTemanSekamar || ""
    ).trim();
    const missionTripHubunganTemanSekamar =
        props.missionTripHubunganTemanSekamar || "";
    const missionTripGscCheck =
        isMissionTripGsc &&
        (akomodasi === "" ||
            (akomodasi !== "tidak" && hotel === "" && tipekamar === ""));
    const missionTripCheck =
        isMissionTrip &&
        (missionTripPelayananAlternatif === "" ||
            (isMissionTripFullPackage &&
                (missionTripPunyaTemanSekamar === "" ||
                    (missionTripPunyaTemanSekamar === "ya" &&
                        (missionTripNamaTemanSekamar === "" ||
                            missionTripNijTemanSekamar === "" ||
                            missionTripHubunganTemanSekamar === "")) ||
                    (missionTripPunyaTemanSekamar === "tidak" &&
                        !props
                            .missionTripBersediaDiaturkanTemanSekamar))));

    const { customForm, customFormData, onChangeCustomFormData } = props;
    const currentSeragamSizeName = isDynamicForm
        ? getSeragamSize(props.selectedEventSub)
        : getSeragamSizeName(props.selectedEventSub.name || "");
    const currentSeragamGroup = isDynamicForm
        ? getSeragamGender(props.selectedEventSub)
        : getSeragamGroup(props.selectedEventSub.name || "");
    const shouldShowSeragamNote =
        isSeragamExclusive &&
        !isDynamicForm &&
        isSeragamCustomSizeSession(props.selectedEventSub.name || "");

    const handleSelectMultiple = (key, opt, checked) => {
        const currentValues = customFormData[key] || [];
        if (checked) {
            onChangeCustomFormData(key, [...currentValues, opt]);
        } else {
            onChangeCustomFormData(key, currentValues.filter((item) => item !== opt));
        }
    };

    const renderField = (field) => {
        switch (field.type) {
            case "text":
            case "number":
            case "date":
            case "datetime":
                return (
                    <Form.Group key={field.key} className="mb-3">
                        <Form.Label>{field.label} {field.required && <span className="text-danger">*</span>}</Form.Label>
                        <InputGroup>
                            {field.prefix && (
                                <InputGroup.Text>{field.prefix}</InputGroup.Text>
                            )}
                            <Form.Control
                                type={field.type === "datetime" ? "datetime-local" : field.type}
                                placeholder={field.placeholder}
                                required={field.required}
                                value={customFormData[field.key] || ""}
                                onChange={(e) => onChangeCustomFormData(field.key, e.target.value)}
                            />
                            {field.suffix && (
                                <InputGroup.Text>{field.suffix}</InputGroup.Text>
                            )}
                        </InputGroup>
                    </Form.Group>
                );
            case "select_single":
                return (
                    <Form.Group key={field.key} className="mb-3">
                        <Form.Label>{field.label} {field.required && <span className="text-danger">*</span>}</Form.Label>
                        <Form.Control
                            as="select"
                            required={field.required}
                            value={customFormData[field.key] || ""}
                            onChange={(e) => onChangeCustomFormData(field.key, e.target.value)}
                        >
                            <option value="">{field.placeholder || "-- Select --"}</option>
                            {field.options && field.options.map((opt, idx) => (
                                <option key={idx} value={opt}>{opt}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                );
            case "select_multiple":
                return (
                    <Form.Group key={field.key} className="mb-3">
                        <Form.Label>{field.label} {field.required && <span className="text-danger">*</span>}</Form.Label>
                        <div>
                            {field.options && field.options.map((opt, idx) => (
                                <Form.Check
                                    key={idx}
                                    type="checkbox"
                                    id={`${field.key}-${idx}`}
                                    label={opt}
                                    checked={(customFormData[field.key] || []).includes(opt)}
                                    onChange={(e) => handleSelectMultiple(field.key, opt, e.target.checked)}
                                />
                            ))}
                        </div>
                    </Form.Group>
                );
            default:
                return null;
        }
    };

    const renderVMForm = () => {
        return (
            <div className="form-group">
                <div style={{ marginTop: "8px" }}>
                    {ministriesLoading ? (
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    ) : ministriesError ? (
                        <div>
                            <div>Failed to load ministries</div>
                            <button
                                className="btn btn-link p-0"
                                onClick={() => window.location.reload()}
                            >
                                Click to refresh
                            </button>
                        </div>
                    ) : ministries !== null ? (
                        <select
                            style={selectStyle}
                            value={props.ministry}
                            name="ministry"
                            id="ministry"
                            onChange={(e) => onChangeMinistry(e.target.value)}
                        >
                            <option value="">-- Select Ministry --</option>
                            {ministries.map((ministry) => (
                                <option
                                    key={`ministry-${ministry.id}`}
                                    value={ministry.id}
                                >
                                    {ministry.name}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <></>
                    )}
                </div>
            </div>
        );
    };

    const addOrderConfirmationDetail = (details, label, value) => {
        if (value === undefined || value === null || value === "") {
            return;
        }

        details.push({
            label,
            value: Array.isArray(value) ? value.join(", ") : value,
        });
    };

    const getOrderConfirmationDetails = () => {
        const details = [];
        const selectedPackageName = isSeragamExclusive
            ? currentSeragamSizeName
            : props.selectedEventSub.name;

        addOrderConfirmationDetail(details, "Event", props.selectedEvent.name);
        addOrderConfirmationDetail(details, "Pilihan", selectedPackageName);
        addOrderConfirmationDetail(details, "Jumlah", props.ticket);

        if (props.selectedEvent.is_paid) {
            const totalPrice =
                Number(props.selectedEventSub.price || 0) *
                Number(props.ticket || 1);
            addOrderConfirmationDetail(
                details,
                "Total",
                `IDR ${totalPrice.toLocaleString()}`
            );
        }

        if (isSeragamExclusive && currentSeragamGroup !== defaultSeragamGroup) {
            addOrderConfirmationDetail(details, "Kategori", currentSeragamGroup);
        }

        if (exclusive === "aogfindingmajor") {
            addOrderConfirmationDetail(
                details,
                "Tingkat/Kelas",
                kelas === "Other" ? kelasOther : kelas
            );
        }

        if (isMissionTripGsc) {
            addOrderConfirmationDetail(details, "Akomodasi", akomodasi);
            addOrderConfirmationDetail(details, "Hotel", hotel);
            addOrderConfirmationDetail(details, "Tipe Kamar", tipekamar);
        }

        if (isMissionTrip) {
            addOrderConfirmationDetail(
                details,
                "Jenis Paket",
                missionTripPackageType
            );
            addOrderConfirmationDetail(
                details,
                "Pelayanan Alternatif",
                missionTripPelayananAlternatif
            );

            if (isMissionTripFullPackage) {
                addOrderConfirmationDetail(
                    details,
                    "Punya Teman Sekamar",
                    missionTripPunyaTemanSekamar === "ya" ? "Ya" : "Tidak"
                );

                if (missionTripPunyaTemanSekamar === "ya") {
                    addOrderConfirmationDetail(
                        details,
                        "Nama Teman Sekamar",
                        missionTripNamaTemanSekamar
                    );
                    addOrderConfirmationDetail(
                        details,
                        "NIJ Teman Sekamar",
                        missionTripNijTemanSekamar
                    );
                    addOrderConfirmationDetail(
                        details,
                        "Hubungan Teman Sekamar",
                        missionTripHubunganTemanSekamar
                    );
                } else if (missionTripPunyaTemanSekamar === "tidak") {
                    addOrderConfirmationDetail(
                        details,
                        "Bersedia Diaturkan Teman Sekamar",
                        props.missionTripBersediaDiaturkanTemanSekamar
                            ? "Ya"
                            : "Tidak"
                    );
                }
            }
        }

        if (isAogHub) {
            addOrderConfirmationDetail(
                details,
                "Pilihan",
                props.aogHubStudyStatus
            );
        }

        if (isAogHub || isAogHubOnline) {
            addOrderConfirmationDetail(
                details,
                isAogHubFacilitator
                    ? "Lokasi Sekarang"
                    : "Kota Asal",
                props.origin
            );
            addOrderConfirmationDetail(details, "Tujuan", props.destination);
        }

        if (customForm && customForm.length > 0) {
            customForm.forEach((field) => {
                addOrderConfirmationDetail(
                    details,
                    field.label,
                    customFormData[field.key]
                );
            });
        }

        if (formDefinition) {
            getAnswerDetails(
                formDefinition,
                props.selectedEventSub,
                customFormData,
                language,
                "ticket"
            ).forEach((detail) =>
                addOrderConfirmationDetail(details, detail.label, detail.value)
            );
        }

        return details;
    };

    const handleOrderSubmit = (event) => {
        event.preventDefault();
        setShowOrderConfirmationModal(true);
    };

    const confirmOrderSubmit = () => {
        setShowOrderConfirmationModal(false);
        props.onSubmitTicket({ preventDefault: () => {} });
    };

    //return
    return (
        <>
            <Modal
                className="modal-order-ticket"
                show={props.showModal}
                onHide={props.handleClose}
            >
                <form
                    action="./status"
                    className="container"
                    onSubmit={handleOrderSubmit}
                >
                    <Modal.Header closeButton>
                        <Modal.Title style={modalTitleStyle}>
                            {props.selectedEvent.name}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {/* Quantity Left */}
                        {props.selectedEventSub.quantity <=
                        minQtyViewSeatsLeft ? (
                            <div className="form-group">
                                <span style={modalLabelStyle}>
                                    {l10n["quota"][language]}
                                </span>
                                <span style={modalValueStyle}>
                                    {props.selectedEventSub.quantity > 0
                                        ? props.selectedEventSub.quantity
                                        : 0}{" "}
                                    {l10n["remaining"][language]}
                                </span>
                            </div>
                        ) : null}

                        {!isSeragamExclusive && !isMissionTripBehavior && (
                        <div style={infoStyle}>
                            {getDateWithoutTimezone(
                                props.selectedEventSub.start_date
                            )}{" "}
                            &#x2022;{" "}
                            {getTimeWithoutTimezone(
                                props.selectedEventSub.start_date
                            )}
                        </div>
                        )}
                        <div style={titleStyle}>
                            {isSeragamExclusive
                                ? currentSeragamSizeName
                                : props.selectedEventSub.name}
                        </div>

                        {isSeragamExclusive &&
                        currentSeragamGroup !== defaultSeragamGroup ? (
                            <div className="form-group">
                                <div
                                    className="event-sub-list__filter-actions"
                                    style={{ justifyContent: "flex-start" }}
                                >
                                    <span
                                        className="event-sub-list__filter-chip event-sub-list__filter-chip--active"
                                        style={seragamLabelChipStyle}
                                    >
                                        {
                                            l10n[
                                                seragamGroupLabels[
                                                    currentSeragamGroup
                                                ] || "seragamGroupDefault"
                                            ][language]
                                        }
                                    </span>
                                </div>
                            </div>
                        ) : null}

                        {/* Service Room Button Selection */}
                        {!isSeragamExclusive && !isMissionTripBehavior && (
                            <div className="form-group">
                                <span style={modalLabelStyle}>
                                    {l10n["room"][language]}
                                </span>
                                <span style={modalValueStyle}>
                                    {/* {props.selectedEventSub.room ? (props.selectedEventSub.room.name) : null} */}
                                </span>
                                <props.roomButton />
                            </div>
                        )}

                        {/* Form for GMS Run */}
                        {props.selectedEvent.exclusive === "aogrun" ? (
                            <>
                                <div className="form-group">
                                    <span style={modalLabelStyle}>
                                        Select Jersey Size.{" "}
                                        <span
                                            onClick={props.showModalChartSize}
                                            style={buttonChartSizeStyle}
                                        >
                                            see size chart
                                        </span>
                                    </span>

                                    {props.selectedEventSub.name
                                        .toUpperCase()
                                        .startsWith("KIDS") ? (
                                        <>
                                            <div style={{ marginTop: "8px" }}>
                                                <select
                                                    style={selectStyle}
                                                    value={props.typeKids}
                                                    name="typeKids"
                                                    id="typeKids"
                                                    onChange={
                                                        props.onChangeSelect
                                                    }
                                                >
                                                    <option value="kids">
                                                        Kids
                                                    </option>
                                                </select>
                                            </div>
                                            <div>
                                                <select
                                                    style={selectStyle}
                                                    value={props.sizeKids}
                                                    name="sizeKids"
                                                    id="sizeKids"
                                                    onChange={
                                                        props.onChangeSelect
                                                    }
                                                >
                                                    <option value="S">S</option>
                                                    <option value="M">M</option>
                                                    <option value="L">L</option>
                                                    <option value="XL">
                                                        XL
                                                    </option>
                                                </select>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ marginTop: "8px" }}>
                                                <select
                                                    style={selectStyle}
                                                    value={props.type}
                                                    name="type"
                                                    id="type"
                                                    onChange={
                                                        props.onChangeSelect
                                                    }
                                                >
                                                    <option value="men">
                                                        Men
                                                    </option>
                                                    <option value="women">
                                                        Women
                                                    </option>
                                                </select>
                                            </div>
                                            {props.type === "men" ? (
                                                <div>
                                                    <select
                                                        style={selectStyle}
                                                        value={props.sizeMen}
                                                        name="sizeMen"
                                                        id="sizeMen"
                                                        onChange={
                                                            props.onChangeSelect
                                                        }
                                                    >
                                                        <option value="S">
                                                            S
                                                        </option>
                                                        <option value="M">
                                                            M
                                                        </option>
                                                        <option value="L">
                                                            L
                                                        </option>
                                                        <option value="XL">
                                                            XL
                                                        </option>
                                                        <option value="2XL">
                                                            2XL
                                                        </option>
                                                        <option value="3XL">
                                                            3XL
                                                        </option>
                                                    </select>
                                                </div>
                                            ) : null}
                                            {props.type === "women" ? (
                                                <div>
                                                    <select
                                                        style={selectStyle}
                                                        value={props.sizeWomen}
                                                        name="sizeWomen"
                                                        id="sizeWomen"
                                                        onChange={
                                                            props.onChangeSelect
                                                        }
                                                    >
                                                        <option value="S">
                                                            S
                                                        </option>
                                                        <option value="M">
                                                            M
                                                        </option>
                                                        <option value="L">
                                                            L
                                                        </option>
                                                        <option value="XL">
                                                            XL
                                                        </option>
                                                        <option value="2XL">
                                                            2XL
                                                        </option>
                                                    </select>
                                                </div>
                                            ) : null}
                                        </>
                                    )}
                                </div>

                                <div className="form-group">
                                    <span style={modalLabelStyle}>
                                        Racepack Pickup
                                    </span>

                                    <div>
                                        <select
                                            style={selectStyle}
                                            value={props.pickup}
                                            name="pickup"
                                            id="pickup"
                                            onChange={props.onChangeSelect}
                                        >
                                            <option value="SCC">
                                                SCC 11-12 Feb (Barat)
                                            </option>
                                            <option value="PCM">
                                                PCM 12 Feb (Timur)
                                            </option>
                                            <option value="MCC">
                                                MCC 12 Feb (Selatan)
                                            </option>
                                            <option value="SCCWD">
                                                SCC 14-16 Feb (Barat Weekdays)
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </>
                        ) : null}

                        {(props.selectedEvent.exclusive === "postmaritalclass" 
                            || props.selectedEvent.exclusive === "postmaritalclassspecial") ? (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>NIJ</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            value={props.nij || ""}
                                            disabled
                                        />
                                    </InputGroup>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nama</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            value={props.name || ""}
                                            disabled
                                        />
                                    </InputGroup>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>WA</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            value={props.phone || ""}
                                            disabled
                                        />
                                    </InputGroup>
                                </Form.Group>
                                {customForm &&
                                    customForm.map((field) =>
                                        renderField(field)
                                    )}
                            </>
                        ) : null}

                        { (props.selectedEvent.exclusive === "gmsrunmanado" || props.selectedEvent.exclusive === "gmsrunsamarinda") ? (
                            <>
                                <div className="form-group">
                                    <span style={modalLabelStyle}>
                                        Select Jersey Size.{" "}
                                        <span
                                            onClick={props.showModalChartSize}
                                            style={buttonChartSizeStyle}
                                        >
                                            see size chart
                                        </span>
                                    </span>

                                    {false ? (
                                        <>
                                            <div style={{ marginTop: "8px" }}>
                                                <select
                                                    style={selectStyle}
                                                    value={props.typeKids}
                                                    name="typeKids"
                                                    id="typeKids"
                                                    onChange={
                                                        props.onChangeSelect
                                                    }
                                                >
                                                    <option value="kids">
                                                        Kids
                                                    </option>
                                                </select>
                                            </div>
                                            <div>
                                                <select
                                                    style={selectStyle}
                                                    value={props.sizeKids}
                                                    name="sizeKids"
                                                    id="sizeKids"
                                                    onChange={
                                                        props.onChangeSelect
                                                    }
                                                >
                                                    <option value="S">S</option>
                                                    <option value="M">M</option>
                                                    <option value="L">L</option>
                                                    <option value="XL">
                                                        XL
                                                    </option>
                                                </select>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {/* <div style={{ marginTop: '8px' }}><select style={selectStyle} value={props.type} name="type" id="type"
                                onChange={props.onChangeSelect}>
                                <option value="men">Men</option>
                                <option value="women">Women</option>
                            </select></div> */}
                                            {props.type === "men" ? (
                                                <div>
                                                    <select
                                                        style={selectStyle}
                                                        value={props.sizeMen}
                                                        name="sizeMen"
                                                        id="sizeMen"
                                                        onChange={
                                                            props.onChangeSelect
                                                        }
                                                    >
                                                        <option value="2XS">
                                                            2XS
                                                        </option>
                                                        <option value="XS">
                                                            XS
                                                        </option>
                                                        <option value="S">
                                                            S
                                                        </option>
                                                        <option value="M">
                                                            M
                                                        </option>
                                                        <option value="L">
                                                            L
                                                        </option>
                                                        <option value="XL">
                                                            XL
                                                        </option>
                                                        <option value="2XL">
                                                            2XL
                                                        </option>
                                                        <option value="3XL">
                                                            3XL
                                                        </option>
                                                        <option value="4XL">
                                                            4XL
                                                        </option>
                                                    </select>
                                                </div>
                                            ) : null}
                                        </>
                                    )}
                                </div>

                                <div className="form-group">
                                    <span style={modalLabelStyle}>
                                        Racepack Pickup
                                    </span>
                                    <span style={modalLabelStyle}>
                                        GMS Samarinda, Jl. Belatuk 12, RT 16, Kelurahan Temindung Permai, Kecamatan Sungai Pinang, Kota Samarinda Kalimantan Timur
                                    </span>
                                    <span style={modalLabelStyle}>
                                        Penukaran Racekit tanggal 9-10 Oktober 2025
                                    </span>
                                    <span style={modalLabelStyle}>
                                        Jam penukaran mulai 09.00 sampai 16.30
                                        WITA
                                    </span>
                                    {/* <div><select style={selectStyle} value={props.pickup} name="pickup" id="pickup"
                                onChange={props.onChangeSelect}>
                            <option value="SCC">SCC 11-12 Feb (Barat)</option>
                            <option value="PCM">PCM 12 Feb (Timur)</option>
                            <option value="MCC">MCC 12 Feb (Selatan)</option>
                            <option value="SCCWD">SCC 14-16 Feb (Barat Weekdays)</option>
                            </select></div> */}
                                </div>
                            </>
                        ) : null}

                        {props.selectedEvent.exclusive &&
                            isVMEvent &&
                            renderVMForm()}

                        {props.selectedEvent.exclusive ===
                        "gmsrunbalikpapan" ? (
                            <>
                                <div className="form-group">
                                    <span style={modalLabelStyle}>
                                        Select Jersey Size.{" "}
                                        <span
                                            onClick={props.showModalChartSize}
                                            style={buttonChartSizeStyle}
                                        >
                                            Click here for the size chart
                                        </span>
                                    </span>

                                    {props.selectedEventSub.name
                                        .toUpperCase()
                                        .startsWith("ANAK") ? (
                                        <>
                                            <div style={{ marginTop: "8px" }}>
                                                <select
                                                    style={selectStyle}
                                                    value={props.typeKids}
                                                    name="typeKids"
                                                    id="typeKids"
                                                    onChange={
                                                        props.onChangeSelect
                                                    }
                                                >
                                                    <option value="kids">
                                                        Kids
                                                    </option>
                                                </select>
                                            </div>
                                            <div>
                                                <select
                                                    style={selectStyle}
                                                    value={props.sizeKids}
                                                    name="sizeKids"
                                                    id="sizeKids"
                                                    onChange={
                                                        props.onChangeSelect
                                                    }
                                                >
                                                    <option value="XS">
                                                        XS
                                                    </option>
                                                    <option value="S">S</option>
                                                    <option value="M">M</option>
                                                    <option value="L">L</option>
                                                    <option value="XL">
                                                        XL
                                                    </option>
                                                    <option value="XXL">
                                                        XXL
                                                    </option>
                                                </select>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ marginTop: "8px" }}>
                                                <select
                                                    style={selectStyle}
                                                    value={props.type}
                                                    name="type"
                                                    id="type"
                                                    onChange={
                                                        props.onChangeSelect
                                                    }
                                                >
                                                    <option value="men">
                                                        Men
                                                    </option>
                                                    <option value="women">
                                                        Women
                                                    </option>
                                                </select>
                                            </div>

                                            {props.type === "men" ? (
                                                <div>
                                                    <select
                                                        style={selectStyle}
                                                        value={props.sizeMen}
                                                        name="sizeMen"
                                                        id="sizeMen"
                                                        onChange={
                                                            props.onChangeSelect
                                                        }
                                                    >
                                                        <option value="XS">
                                                            XS
                                                        </option>
                                                        <option value="S">
                                                            S
                                                        </option>
                                                        <option value="M">
                                                            M
                                                        </option>
                                                        <option value="L">
                                                            L
                                                        </option>
                                                        <option value="XL">
                                                            XL
                                                        </option>
                                                        <option value="XXL">
                                                            XXL
                                                        </option>
                                                        <option value="XXXL">
                                                            XXXL
                                                        </option>
                                                        <option value="XXXXL">
                                                            XXXXL
                                                        </option>
                                                    </select>
                                                </div>
                                            ) : null}

                                            {props.type === "women" ? (
                                                <div>
                                                    <select
                                                        style={selectStyle}
                                                        value={props.sizeWomen}
                                                        name="sizeWomen"
                                                        id="sizeWomen"
                                                        onChange={
                                                            props.onChangeSelect
                                                        }
                                                    >
                                                        <option value="XS">
                                                            XS
                                                        </option>
                                                        <option value="S">
                                                            S
                                                        </option>
                                                        <option value="M">
                                                            M
                                                        </option>
                                                        <option value="L">
                                                            L
                                                        </option>
                                                        <option value="XL">
                                                            XL
                                                        </option>
                                                        <option value="XXL">
                                                            XXL
                                                        </option>
                                                    </select>
                                                </div>
                                            ) : null}
                                        </>
                                    )}
                                </div>

                                <div className="form-group">
                                    <span style={modalLabelStyle}>
                                        Racepack Pickup
                                    </span>
                                    <span style={modalLabelStyle}>
                                        Racekit di GMS Balikpapan, Jl. Mayjend
                                        Sutoyo Rt.44 No.32
                                    </span>
                                    <span style={modalLabelStyle}>
                                        Penukaran Racekit tanggal 04 Agustus
                                        2024
                                    </span>
                                    <span style={modalLabelStyle}>
                                        Jam penukaran di office hour mulai 10.00
                                        sampai 17.00 WITA
                                    </span>
                                </div>
                            </>
                        ) : null}

                        {/* Form for GMS Run */}
                        {props.selectedEvent.exclusive === "aogfindingmajor" ? (
                            <>
                                <div className="form-group">
                                    <span style={modalLabelStyle}>
                                        Tingkat/Kelas
                                    </span>
                                    <div>
                                        {Object.keys(kelasOptions).map(
                                            (kelasOptionId, i) => (
                                                <Form.Check
                                                    type={"radio"}
                                                    key={i}
                                                    id={`radioFindingMajor-${kelasOptionId}`}
                                                    name={`kelas`}
                                                    value={kelasOptionId}
                                                    label={
                                                        kelasOptions[
                                                            kelasOptionId
                                                        ]
                                                    }
                                                    checked={
                                                        kelas === kelasOptionId
                                                    }
                                                    onChange={() =>
                                                        setKelas(kelasOptionId)
                                                    }
                                                />
                                            )
                                        )}
                                        <Form.Check
                                            type={"radio"}
                                            id={`radioFindingMajor-Other`}
                                            name={`kelas`}
                                            label={
                                                <>
                                                    Lainnya{" "}
                                                    <Form.Control
                                                        ref={txtOther}
                                                        className={
                                                            otherChecked
                                                                ? ""
                                                                : "d-none"
                                                        }
                                                        required={otherChecked}
                                                        type="text"
                                                        value={kelasOther}
                                                        onChange={(e) =>
                                                            setKelasOther(
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </>
                                            }
                                            value={"Other"}
                                            checked={otherChecked}
                                            onChange={() => setKelas("Other")}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : null}
                        {/* Form for Mission Trip GSC */}
                        {isMissionTripGsc ? (
                            <>
                                <div className="form-group">
                                    <span style={modalLabelStyle}>
                                        1. Apakah anda membutuhkan akomodasi kamar hotel sebelum tanggal 16 ?
                                    </span>
                                    <div>
                                        {Object.keys(akomodasiOptions).map(
                                            (akomodasiOptionId, i) => (
                                                <Form.Check
                                                    type={"radio"}
                                                    key={i}
                                                    id={`radioAkomodasi-${akomodasiOptionId}`}
                                                    name={`akomodasi`}
                                                    value={akomodasiOptionId}
                                                    label={
                                                        akomodasiOptions[
                                                            akomodasiOptionId
                                                        ]
                                                    }
                                                    checked={
                                                        akomodasi === akomodasiOptionId
                                                    }
                                                    onChange={() =>
                                                        setAkomodasi(akomodasiOptionId)
                                                    }
                                                />
                                                
                                            )
                                        )}
                                    </div>{akomodasiChecked &&
                                    <span style={modalLabelStyle}>
                                        2. Hotel apa yang akan Anda pesan?
                                    </span>}
                                    <div>
                                        {akomodasiChecked && Object.keys(hotelOptions).map(
                                            (hotelOptionId, i) => (
                                                <Form.Check
                                                    type={"radio"}
                                                    key={i}
                                                    id={`radioHotel-${hotelOptionId}`}
                                                    name={`hotel`}
                                                    value={hotelOptionId}
                                                    label={
                                                        hotelOptions[
                                                            hotelOptionId
                                                        ]
                                                    }
                                                    checked={
                                                        hotel === hotelOptionId
                                                    }
                                                    onChange={() => 
                                                        setHotel(hotelOptionId)
                                                    }
                                                />
                                            )
                                        )}
                                    </div>{akomodasiChecked &&
                                    <span style={modalLabelStyle}>
                                        3. Tipe kamar yang Anda ingin pesan?
                                    </span>}
                                    <div>
                                        {akomodasiChecked && Object.keys(tipekamarOptions).map(
                                            (tipekamarOptionId, i) => (
                                                <Form.Check
                                                    type={"radio"}
                                                    key={i}
                                                    id={`radioTipeKamar-${tipekamarOptionId}`}
                                                    name={`tipekamar`}
                                                    value={tipekamarOptionId}
                                                    label={
                                                        tipekamarOptions[
                                                            tipekamarOptionId
                                                        ]
                                                    }
                                                    checked={
                                                        tipekamar === tipekamarOptionId
                                                    }
                                                    onChange={() => 
                                                        setTipeKamar(tipekamarOptionId)
                                                    }
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : null}

                        {/* Form for Mission Trip */}
                        {isMissionTrip ? (
                            <>
                                {missionTripBannerUrl ? (
                                    <div className="form-group">
                                        <PreviewableImage
                                            buttonClassName="mission-trip-package-banner-button"
                                            hintText={
                                                l10n.previewImageHint[language]
                                            }
                                            imageClassName="img-fluid"
                                            imageStyle={missionTripBannerStyle}
                                            previewTitle={
                                                props.selectedEventSub.name
                                            }
                                            resetText={l10n.reset[language]}
                                            src={missionTripBannerUrl}
                                            alt={props.selectedEventSub.name}
                                            zoomLabel={
                                                l10n.previewZoom[language]
                                            }
                                        />
                                    </div>
                                ) : null}

                                <div
                                    className="form-group"
                                    style={missionTripSuccessCardStyle}
                                >
                                    {isMissionTripFullPackage ? (
                                        <>
                                            <div
                                                style={
                                                    missionTripSuccessHeadingStyle
                                                }
                                            >
                                                Paket Akomodasi sudah termasuk:
                                            </div>
                                            <ul style={missionTripSuccessListStyle}>
                                                <li>
                                                    Hotel 3 malam (harga yang tertera adalah untuk satu orang, 1 kamar bisa untuk 2 orang)
                                                </li>
                                                <li>Sarapan pagi setiap hari</li>
                                                <li>
                                                    Transportasi Airport - Hotel -
                                                    GMS/Venue KKR selama kegiatan
                                                    berlangsung
                                                </li>
                                                <li>
                                                    Voucher makan (nasi kotak)
                                                    untuk 2 hari pelayanan di STB
                                                    dan KKR
                                                </li>
                                            </ul>
                                            <div
                                                style={
                                                    missionTripSuccessHeadingStyle
                                                }
                                            >
                                                Paket Akomodasi tidak termasuk:
                                            </div>
                                            <ul style={missionTripSuccessListStyle}>
                                                <li>
                                                    Tiket pesawat menuju dan dari
                                                    Kota Kupang
                                                </li>
                                            </ul>
                                        </>
                                    ) : (
                                        <>
                                            <div
                                                style={
                                                    missionTripSuccessHeadingStyle
                                                }
                                            >
                                                Paket Nasi Kotak:
                                            </div>
                                            <ul style={missionTripSuccessListStyle}>
                                                <li>
                                                    {missionTripMealboxServiceTerm}
                                                </li>
                                                <li>
                                                    Pembelian voucher dilakukan
                                                    sesuai dengan jadwal pelayanan
                                                    masing-masing.
                                                </li>
                                                <li>
                                                    Peserta yang tidak memiliki
                                                    jadwal pelayanan tidak
                                                    diperkenankan membeli voucher
                                                    ini.
                                                </li>
                                                <li>
                                                    Peserta yang telah membeli
                                                    Paket Akomodasi tidak perlu
                                                    membeli voucher nasi kotak
                                                    secara terpisah karena
                                                    fasilitas makan sudah
                                                    termasuk dalam paket.
                                                </li>
                                            </ul>
                                        </>
                                    )}
                                </div>

                                <div
                                    className="form-group"
                                    style={missionTripWarningCardStyle}
                                >
                                    <div style={missionTripWarningTitleStyle}>
                                        Ketentuan:
                                    </div>
                                    <ul style={missionTripWarningListStyle}>
                                        {missionTripGeneralTerms.map((term) => (
                                            <li key={term}>{term}</li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="form-group">
                                    <button
                                        type="button"
                                        style={missionTripScheduleCardStyle}
                                        onClick={() =>
                                            setShowMissionTripScheduleModal(true)
                                        }
                                    >
                                        <span style={missionTripScheduleCardTextStyle}>
                                            <span
                                                style={
                                                    missionTripScheduleCardTitleStyle
                                                }
                                            >
                                                Jadwal Mission Trip Kupang
                                            </span>
                                            <span
                                                style={
                                                    missionTripScheduleCardCaptionStyle
                                                }
                                            >
                                                STB & KKR
                                            </span>
                                        </span>
                                        <span style={missionTripScheduleCardActionStyle}>
                                            Lihat Jadwal
                                        </span>
                                    </button>
                                </div>

                                {isMissionTripFullPackage ? (
                                    <>
                                        <div className="form-group">
                                            <span style={missionTripQuestionLabelStyle}>
                                                Apakah Anda sudah memiliki teman
                                                sekamar?
                                            </span>
                                            <Form.Check
                                                type="radio"
                                                id="missionTripPunyaTemanSekamar-ya"
                                                name="missionTripPunyaTemanSekamar"
                                                value="ya"
                                                label="Ya"
                                                checked={
                                                    missionTripPunyaTemanSekamar ===
                                                    "ya"
                                                }
                                                onChange={() =>
                                                    props.onChangeMissionTripPunyaTemanSekamar(
                                                        "ya"
                                                    )
                                                }
                                            />
                                            <Form.Check
                                                type="radio"
                                                id="missionTripPunyaTemanSekamar-tidak"
                                                name="missionTripPunyaTemanSekamar"
                                                value="tidak"
                                                label="Tidak"
                                                checked={
                                                    missionTripPunyaTemanSekamar ===
                                                    "tidak"
                                                }
                                                onChange={() =>
                                                    props.onChangeMissionTripPunyaTemanSekamar(
                                                        "tidak"
                                                    )
                                                }
                                            />
                                        </div>

                                        {missionTripPunyaTemanSekamar === "ya" ? (
                                            <>
                                                <div className="form-group">
                                                    <span style={missionTripQuestionLabelStyle}>
                                                        Nama teman sekamar
                                                    </span>
                                                    <Form.Control
                                                        type="text"
                                                        value={
                                                            props.missionTripNamaTemanSekamar ||
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            props.onChangeMissionTripNamaTemanSekamar(
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <span style={missionTripQuestionLabelStyle}>
                                                        NIJ teman sekamar
                                                    </span>
                                                    <Form.Control
                                                        type="text"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        value={
                                                            props.missionTripNijTemanSekamar ||
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            props.onChangeMissionTripNijTemanSekamar(
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <span style={missionTripQuestionLabelStyle}>
                                                        Hubungan teman sekamar
                                                    </span>
                                                    {missionTripRoommateRelationshipOptions.map(
                                                        (relationship) => (
                                                            <Form.Check
                                                                type="radio"
                                                                key={
                                                                    relationship
                                                                }
                                                                id={`missionTripHubunganTemanSekamar-${relationship}`}
                                                                name="missionTripHubunganTemanSekamar"
                                                                value={
                                                                    relationship
                                                                }
                                                                label={
                                                                    relationship
                                                                }
                                                                checked={
                                                                    missionTripHubunganTemanSekamar ===
                                                                    relationship
                                                                }
                                                                onChange={() =>
                                                                    props.onChangeMissionTripHubunganTemanSekamar(
                                                                        relationship
                                                                    )
                                                                }
                                                            />
                                                        )
                                                    )}
                                                    <div
                                                        style={
                                                            missionTripRoommateNoteStyle
                                                        }
                                                    >
                                                        Peserta akan dihubungi oleh
                                                        Panitia untuk memastikan teman
                                                        sekamar.
                                                    </div>
                                                </div>
                                            </>
                                        ) : null}

                                        {missionTripPunyaTemanSekamar ===
                                        "tidak" ? (
                                            <div className="form-group">
                                                <Form.Check
                                                    type="checkbox"
                                                    id="missionTripBersediaDiaturkanTemanSekamar"
                                                    name="missionTripBersediaDiaturkanTemanSekamar"
                                                    label="Anda bersedia diaturkan teman sekamar oleh Panitia."
                                                    checked={
                                                        !!props.missionTripBersediaDiaturkanTemanSekamar
                                                    }
                                                    onChange={(e) =>
                                                        props.onChangeMissionTripBersediaDiaturkanTemanSekamar(
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            </div>
                                        ) : null}
                                    </>
                                ) : null}

                                <div className="form-group">
                                    <span style={missionTripQuestionLabelStyle}>
                                        Jika ministry yang Anda layani saat ini
                                        sudah penuh, bersediakah Anda membantu
                                        melayani di ministry lain?
                                    </span>
                                    {missionTripMinistryOptions.map((option) => (
                                        <Form.Check
                                            type="radio"
                                            key={option}
                                            id={`missionTripPelayananAlternatif-${option}`}
                                            name="missionTripPelayananAlternatif"
                                            value={option}
                                            label={option}
                                            checked={
                                                missionTripPelayananAlternatif ===
                                                option
                                            }
                                            onChange={() =>
                                                props.onChangeMissionTripPelayananAlternatif(
                                                    option
                                                )
                                            }
                                        />
                                    ))}
                                </div>
                            </>
                        ) : null}

                        {/* Form for AOG Bootcamp */}
                        {(props.selectedEvent.exclusive === "aogbootcamp1" || exclusive === "aogbootcamp1regional" || exclusive === "aogbootcamp2" || exclusive === "aogbootcamp2regional") ? (
                            <>
                                {/* jenisPendaftaran Field */}
                                <div className="form-group">
                                    <span style={modalLabelStyle}>
                                        Jenis Pendaftaran
                                    </span>
                                    <div>
                                        {Object.keys(
                                            registrationTypeOptions
                                        ).map((registrationTypeOptionId, i) => (
                                            <Form.Check
                                                type={"radio"}
                                                key={i}
                                                id={`radioRegistrationTypeAOGBootcamp-${registrationTypeOptionId}`}
                                                name={`registrationType`}
                                                value={registrationTypeOptionId}
                                                label={
                                                    registrationTypeOptions[
                                                        registrationTypeOptionId
                                                    ]
                                                }
                                                checked={
                                                    registrationType ===
                                                    registrationTypeOptionId
                                                }
                                                onChange={() => {
                                                    setRegistrationType(
                                                        registrationTypeOptionId
                                                    );

                                                    if (registrationTypeOptionId === 'mandiri') {
                                                        onChangeRegistrantName("");
                                                        onChangeIsCG(false);
                                                        onChangeRegistrantAge("");
                                                        setGender("pria");
                                                    }
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                                {/* Registrant Name */}
                                <hr />
                                {registrationType === 'wali' && (
                                    <div className="form-group">
                                        <span style={modalLabelStyle}>
                                            Nama Lengkap
                                        </span>
                                        <Form.Control
                                            type="text"
                                            placeholder="Masukkan Nama"
                                            value={registrantName}
                                            onChange={(e) => {
                                                onChangeRegistrantName(
                                                    e.target.value
                                                );
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Registrant CG Status*/}
                                {registrationType === 'wali' && (
                                
                                    <div className="form-group">
                                        <Form.Check
                                            type="checkbox"
                                            id="checkbox-isCG"
                                            label="Sudah Bergabung CG"
                                            checked={isCG}
                                            onChange={() => {
                                                onChangeIsCG(!isCG);
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Registrant Age */}
                                {registrationType === 'wali' && (
                                    <div className="form-group">
                                        <span style={modalLabelStyle}>
                                            Usia
                                        </span>
                                        <Form.Control
                                            type="number"
                                            placeholder="Masukkan Usia"
                                            value={registrantAge}
                                            onChange={(e) => {
                                                onChangeRegistrantAge(
                                                    e.target.value
                                                );
                                            }}
                                        />
                                    </div>
                                )}
                                
                                {/* Registrant Gender */}
                                {registrationType === 'wali' && (
                                    <div className="form-group">
                                        <span style={modalLabelStyle}>
                                            Jenis Kelamin
                                        </span>
                                        <div>
                                            {Object.keys(genderOptions).map(
                                                (genderOptionId, i) => (
                                                    <Form.Check
                                                        type={"radio"}
                                                        key={i}
                                                        id={`radioGenderAOGBootcamp-${genderOptionId}`}
                                                        name={`gender`}
                                                        value={genderOptionId}
                                                        label={
                                                            genderOptions[
                                                                genderOptionId
                                                            ]
                                                        }
                                                        checked={
                                                            gender ===
                                                            genderOptionId
                                                        }
                                                        onChange={() => {
                                                            setGender(
                                                                genderOptionId
                                                            );
                                                        }}
                                                    />
                                                )
                                            )}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Registrant Phone Number*/}
                                <div className="form-group">
                                    <span style={modalLabelStyle}>
                                        Nomor Telepon Peserta
                                    </span>
                                    <Form.Control
                                        type="text"
                                        placeholder="Masukkan Nomor Telepon"
                                        value={props.phoneNumber}
                                        onChange={(e) => {
                                            let newValue = e.target.value;

                                            // Your formatting logic
                                            if (newValue.startsWith("+620")) {
                                                newValue = newValue.replace(
                                                    /^(\+620)+/,
                                                    "+62"
                                                );
                                            } else if (
                                                newValue.startsWith("+62")
                                            ) {
                                                newValue = newValue.replace(
                                                    /^(\+62)+/,
                                                    ""
                                                );
                                            } else if (
                                                newValue.startsWith("+6")
                                            ) {
                                                newValue = newValue.replace(
                                                    /^(\+6)+/,
                                                    ""
                                                );
                                            } else if (
                                                newValue.startsWith("0")
                                            ) {
                                                newValue = newValue.replace(
                                                    /^(0)+/,
                                                    ""
                                                );
                                            }

                                            if (/^\d{0,12}$/.test(newValue)) {
                                                onChangePhoneNumber(
                                                    "+62" + newValue
                                                );
                                            }
                                        }}
                                    />
                                </div>

                                {/* Registrant Medical Condition */}
                                <div className="form-group">
                                    <span style={modalLabelStyle}>
                                        Alergi dan Riwayat Penyakit
                                    </span>
                                    <Form.Control
                                        as="textarea"
                                        placeholder="Masukkan Alergi dan Riwayat Penyakit"
                                        value={props.medicalCondition}
                                        onChange={(e) => {
                                            onChangeMedicalCondition(
                                                e.target.value
                                            );
                                        }}
                                    />
                                </div>
                            </>
                        ) : null}

                        { (props.selectedEvent.exclusive === "christmascelebrationforvip" || 
                          props.selectedEvent.exclusive === "christmascelebrationforvip1" || 
                          props.selectedEvent.exclusive === "christmascelebrationforvip2" || 
                          props.selectedEvent.exclusive === "christmascelebrationforvip3" || 
                          props.selectedEvent.exclusive === "christmascelebrationforvip4" || 
                          props.selectedEvent.exclusive === "christmascelebrationforvip5" || 
                          props.selectedEvent.exclusive === "stbinvitation") ? (
                            <>
                                <div className="form-group">
                                    <span style={modalLabelStyle}>
                                        Catatan *
                                    </span>
                                    <Form.Control
                                        type="text"
                                        placeholder="Masukkan catatan nama VIP"
                                        value={catatan}
                                        onChange={(e) => {
                                            onChangeCatatan(e.target.value);
                                        }}
                                    />
                                </div>
                            </>
                        ) : null}

                        {(isAogHub || isAogHubOnline) && (
                            <>
                                {isAogHub && (
                                    <>
                                        <Form.Check
                                            type={"radio"}
                                            id="aogHubStudyStatus-facilitator"
                                            name={`aogHubStudyStatus`}
                                            value={AOG_HUB_FACILITATOR}
                                            label={AOG_HUB_FACILITATOR}
                                            checked={
                                                props.aogHubStudyStatus ===
                                                AOG_HUB_FACILITATOR
                                            }
                                            onChange={() =>
                                                props.onChangeAogHubStudyStatus(
                                                    AOG_HUB_FACILITATOR
                                                )
                                            }
                                        />
                                        <Form.Check
                                            type={"radio"}
                                            id="aogHubStudyStatus-plan-abroad"
                                            name={`aogHubStudyStatus`}
                                            value={AOG_HUB_PLAN_ABROAD}
                                            label={AOG_HUB_PLAN_ABROAD}
                                            checked={
                                                props.aogHubStudyStatus ===
                                                AOG_HUB_PLAN_ABROAD
                                            }
                                            onChange={() =>
                                                props.onChangeAogHubStudyStatus(
                                                    AOG_HUB_PLAN_ABROAD
                                                )
                                            }
                                        />
                                        <Form.Check
                                            type={"radio"}
                                            id="aogHubStudyStatus-plan-domestic"
                                            name={`aogHubStudyStatus`}
                                            value={AOG_HUB_PLAN_DOMESTIC}
                                            label={AOG_HUB_PLAN_DOMESTIC}
                                            checked={
                                                props.aogHubStudyStatus ===
                                                AOG_HUB_PLAN_DOMESTIC
                                            }
                                            onChange={() =>
                                                props.onChangeAogHubStudyStatus(
                                                    AOG_HUB_PLAN_DOMESTIC
                                                )
                                            }
                                        />
                                    </>
                                )}
                                {(isAogHubOnline ||
                                    isAogHubFacilitator ||
                                    isAogHubPlanning) && (
                                    <div className="form-group">
                                        <span style={modalLabelStyle}>
                                            {isAogHubFacilitator
                                                ? "Sedang berada di kota / negara mana?"
                                                : "Kota Asal"}
                                        </span>
                                        <Form.Control
                                            type="text"
                                            value={props.origin}
                                            onChange={(e) => {
                                                props.onChangeOrigin(e.target.value);
                                            }}
                                        />
                                    </div>
                                )}
                                {(isAogHubOnline || isAogHubPlanning) && (
                                    <div className="form-group">
                                        <span style={modalLabelStyle}>
                                            {isAogHubOnline
                                                ? "Kota / Negara Tujuan"
                                                : props.aogHubStudyStatus ===
                                                  AOG_HUB_PLAN_ABROAD
                                                    ? "Negara Tujuan"
                                                    : "Kota Tujuan"}
                                        </span>
                                        <Form.Control
                                            type="text"
                                            value={props.destination}
                                            onChange={(e) => {
                                                props.onChangeDestination(e.target.value);
                                            }}
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {formDefinition ? (
                            <DynamicRegistrationForm
                                definition={formDefinition}
                                session={props.selectedEventSub}
                                answers={customFormData}
                                onChange={onChangeCustomFormData}
                                language={language}
                            />
                        ) : null}

                        {/* Service Number of Ticket Button */}
                        <div className="form-group">
                            <span style={modalLabelStyle}>
                                {isMissionTripGsc
                                    ? l10n["numbersOfRooms"][language]
                                    : l10n["numbersOfTicket"][language]}
                            </span>
                            <span>
                                <input
                                    type="hidden"
                                    name="ticket"
                                    value={props.ticket}
                                    onChange={props.onChangeTicket}
                                />
                                {Number(props.maxTicketOrder) <= 5 ? (
                                    <props.ticketButton />
                                ) : (
                                    <div className="form-group">
                                        <InputGroup className="mb-1">
                                            <Button
                                                variant="primary"
                                                onClick={decreaseTicketQty}
                                                className="mr-3"
                                            >
                                                -
                                            </Button>
                                            <Form.Control
                                                type="number"
                                                className="text-center"
                                                value={ticketQty}
                                                onChange={(e) =>
                                                    setTicketQty(e.target.value)
                                                }
                                                onBlur={onInputTicketBlur}
                                            />
                                            <Button
                                                variant="primary"
                                                onClick={increaseTicketQty}
                                                className="ml-3"
                                            >
                                                +
                                            </Button>
                                        </InputGroup>

                                        <span style={modalLabelStyle}>
                                            {l10n["infoMaxTicket"][language]}{" "}
                                            <b>{props.maxTicketOrder}</b>{" "}
                                            {l10n["ticket"][language]}
                                        </span>
                                    </div>
                                )}
                            </span>
                        </div>

                        {shouldShowSeragamNote ? (
                            <div className="form-group">
                                <span style={modalLabelStyle}>
                                    {l10n["note"][language]}
                                </span>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder={
                                        l10n["enterAdditionalNotes"][language]
                                    }
                                    value={catatan}
                                    onChange={(e) => {
                                        onChangeCatatan(e.target.value);
                                    }}
                                />
                            </div>
                        ) : null}

                        {/* Overflow Agreement -- hide at GMS Run */}
                        <div style={{ display: "none" }}>
                            <div className="row d-flex align-items-center">
                                <div className="col-2">
                                    <input
                                        type="checkbox"
                                        name="agreement"
                                        value={agreeOverflow}
                                        checked={agreeOverflow}
                                        onChange={() =>
                                            setAgreeOverflow(!agreeOverflow)
                                        }
                                        style={agreementCheckStyle}
                                    />
                                </div>
                                <div
                                    className="col-10"
                                    onClick={() =>
                                        setAgreeOverflow(!agreeOverflow)
                                    }
                                    value={agreeOverflow}
                                >
                                    {l10n["overflowAgree"][language]}
                                </div>
                            </div>
                        </div>
                        {/* Service Price */}
                        {props.selectedEvent.is_paid ? (
                            <div className="form-group">
                                <span style={modalLabelStyle}>
                                    {l10n["price"][language]}
                                </span>
                                <span style={modalValuePriceStyle}>
                                    {props.selectedEventSub.price
                                        ? "IDR " +
                                          (
                                              props.selectedEventSub.price *
                                              props.ticket
                                          ).toLocaleString()
                                        : 0}
                                </span>
                            </div>
                        ) : null}
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={props.handleClose}>
                            {l10n["cancel"][language]}
                        </Button>
                        <input
                            disabled={
                                props.selectedEventSub.quantity <= 0 ||
                                aogFindingMajorCheck ||
                                aogBootcampCheck ||
                                christmasVipCheck ||
                                aogHubCheck ||
                                aogHubOnlineCheck ||
                                vmCheck ||
                                missionTripGscCheck ||
                                missionTripCheck /* || agreeOverflow === false*/
                            }
                            style={modalSubmitStyle}
                            type="submit"
                            value={l10n["order"][language]}
                            className="btn btn-primary"
                        />
                    </Modal.Footer>
                </form>
            </Modal>
            <Modal
                show={showOrderConfirmationModal}
                onHide={() => setShowOrderConfirmationModal(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title style={orderConfirmationTitleStyle}>
                        Konfirmasi Pesanan
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={orderConfirmationQuestionStyle}>
                        Apakah Anda yakin dengan pesanan ini?
                    </p>
                    <div style={orderConfirmationDetailCardStyle}>
                        {getOrderConfirmationDetails().map((detail) => (
                            <div
                                key={`${detail.label}-${detail.value}`}
                                style={orderConfirmationDetailRowStyle}
                            >
                                <span style={orderConfirmationDetailLabelStyle}>
                                    {detail.label}
                                </span>
                                <span style={orderConfirmationDetailValueStyle}>
                                    {detail.value}
                                </span>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowOrderConfirmationModal(false)}
                    >
                        {l10n["no"][language]}
                    </Button>
                    <Button variant="primary" onClick={confirmOrderSubmit}>
                        {l10n["yes"][language]}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showMissionTripScheduleModal}
                onHide={() => setShowMissionTripScheduleModal(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Jadwal Mission Trip Kupang</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {missionTripScheduleImages.map((imageUrl, imageIndex) => (
                        <img
                            key={imageUrl}
                            className="img-fluid"
                            src={imageUrl}
                            alt={`Jadwal Mission Trip Kupang ${imageIndex + 1}`}
                            style={missionTripScheduleImageStyle}
                        />
                    ))}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowMissionTripScheduleModal(false)}
                    >
                        Tutup
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

// Styles
const modalTitleStyle = {
    color: "#555",
    fontSize: "0.9rem",
    fontWeight: "bold",
};

const modalSubmitStyle = {
    // textTransform: 'uppercase',
};

const orderConfirmationTitleStyle = {
    color: "#555",
    fontSize: "0.95rem",
    fontWeight: "bold",
};
const orderConfirmationQuestionStyle = {
    color: "#423f40",
    fontSize: "0.92rem",
    marginBottom: "12px",
};
const orderConfirmationDetailCardStyle = {
    backgroundColor: "#f8f9fa",
    border: "1px solid #e9ecef",
    borderRadius: "6px",
    padding: "10px 12px",
};
const orderConfirmationDetailRowStyle = {
    display: "flex",
    gap: "12px",
    justifyContent: "space-between",
    padding: "7px 0",
};
const orderConfirmationDetailLabelStyle = {
    color: "#6c757d",
    flex: "0 0 42%",
    fontSize: "0.82rem",
};
const orderConfirmationDetailValueStyle = {
    color: "#231f20",
    flex: "1",
    fontSize: "0.86rem",
    fontWeight: "700",
    textAlign: "right",
    wordBreak: "break-word",
};

const agreementCheckStyle = {
    width: "24px",
    height: "24px",
    cursor: "pointer",
};

const modalLabelStyle = {
    fontSize: "0.75rem",
    color: "#888",
    marginBottom: "2px",
    display: "block",
    fontWeight: "400",
};

const modalValueStyle = {
    fontSize: "1.05rem",
    color: "#555",
    marginBottom: "12px",
    display: "block",
    fontWeight: "bold",
};
const infoStyle = {
    color: "#423f40",
    fontSize: "0.85rem",
};
const titleStyle = {
    fontSize: "1rem",
    fontWeight: "bold",
    marginBottom: "10px",
};
const modalValuePriceStyle = {
    fontSize: "1.05rem",
    color: "#dc3545",
    marginBottom: "12px",
    display: "block",
    fontWeight: "bold",
};
const selectStyle = {
    fontSize: "1.2rem",
    width: "100%",
    padding: "4px",
};
const buttonChartSizeStyle = {
    color: "#423f40",
    fontSize: "0.9rem",
    fontWeight: "bold",
    marginBottom: "4px",
};
const seragamLabelChipStyle = {
    padding: "6px 10px",
    fontSize: "0.78rem",
    gap: "0",
    minHeight: "unset",
};
const missionTripBannerStyle = {
    borderRadius: "6px",
    width: "100%",
};
const missionTripSuccessCardStyle = {
    backgroundColor: "#f0fff4",
    border: "1px solid #9ae6b4",
    borderLeft: "4px solid #28a745",
    borderRadius: "6px",
    padding: "12px",
};
const missionTripSuccessHeadingStyle = {
    color: "#276749",
    fontSize: "0.92rem",
    fontWeight: "600",
    marginBottom: "6px",
};
const missionTripSuccessListStyle = {
    color: "#2f4f3a",
    fontSize: "0.92rem",
    lineHeight: "1.45",
    marginBottom: "12px",
    paddingLeft: "18px",
};
const missionTripWarningCardStyle = {
    backgroundColor: "#fff5f5",
    border: "1px solid #feb2b2",
    borderLeft: "4px solid #dc3545",
    borderRadius: "6px",
    color: "#842029",
    padding: "12px 14px",
};
const missionTripWarningTitleStyle = {
    fontSize: "0.92rem",
    fontWeight: "700",
    marginBottom: "6px",
};
const missionTripWarningListStyle = {
    fontSize: "0.88rem",
    fontWeight: "600",
    lineHeight: "1.45",
    marginBottom: 0,
    paddingLeft: "18px",
};
const missionTripQuestionLabelStyle = {
    color: "#555",
    display: "block",
    fontSize: "0.92rem",
    fontWeight: "600",
    marginBottom: "6px",
};
const missionTripRoommateNoteStyle = {
    backgroundColor: "#fff7e6",
    border: "1px solid #ffd591",
    borderLeft: "4px solid #fa8c16",
    borderRadius: "6px",
    color: "#7a4a00",
    fontSize: "0.86rem",
    fontWeight: "700",
    lineHeight: "1.4",
    marginTop: "10px",
    padding: "10px 12px",
};
const missionTripScheduleCardStyle = {
    alignItems: "center",
    backgroundColor: "#f5f9ff",
    border: "1px solid #8bb7f0",
    borderLeft: "5px solid #007bff",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 123, 255, 0.12)",
    color: "#1f2937",
    cursor: "pointer",
    display: "flex",
    gap: "12px",
    justifyContent: "space-between",
    padding: "14px 16px",
    textAlign: "left",
    width: "100%",
};
const missionTripScheduleCardTextStyle = {
    minWidth: 0,
};
const missionTripScheduleCardTitleStyle = {
    display: "block",
    fontSize: "0.95rem",
    fontWeight: "700",
    marginBottom: "2px",
};
const missionTripScheduleCardCaptionStyle = {
    color: "#666",
    display: "block",
    fontSize: "0.86rem",
    lineHeight: "1.35",
};
const missionTripScheduleCardActionStyle = {
    backgroundColor: "#007bff",
    borderRadius: "999px",
    color: "#fff",
    display: "inline-flex",
    flexShrink: 0,
    fontSize: "0.78rem",
    fontWeight: "700",
    lineHeight: "1",
    padding: "8px 10px",
    whiteSpace: "nowrap",
};
const missionTripScheduleImageStyle = {
    borderRadius: "6px",
    marginBottom: "16px",
    width: "100%",
};
